import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { firebaseAuth, authInstance } from '../services/api/firebaseAdapter.jsx';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  // Email captured from the kiosk email-gate modal (stored locally, sent with orders)
  const [capturedEmail, setCapturedEmail] = useState('');
  
  // Kept for backward compatibility if needed, but mostly unused now
  const [authModal, setAuthModal] = useState({ isOpen: false, action: null });
  const openAuthModal = (action) => setAuthModal({ isOpen: true, action });
  const closeAuthModal = () => setAuthModal({ isOpen: false, action: null });

  useEffect(() => {
    let unsubscribe = () => {};

    // Register the auth state listener IMMEDIATELY — do not wait for setPersistence.
    // onAuthStateChanged fires with the already-persisted session straight away,
    // so isAuthLoading resolves as fast as possible and unblocks OrderProvider/QueueProvider.
    unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setIsGuest(user ? user.isAnonymous : false);
      setIsAuthLoading(false);
    });

    // Set persistence concurrently in the background.
    // It only affects FUTURE sign-in calls — not the current auth state — so
    // running it after the listener is registered is safe and correct.
    setPersistence(authInstance, browserLocalPersistence).catch((error) => {
      console.error("Auth persistence error:", error);
    });

    return () => unsubscribe();
  }, []);

  const signInAsGuest = async () => {
    return await firebaseAuth.signInAnonymously();
  };

  /**
   * Sign in anonymously and capture the customer's email.
   * Used in kiosk modes that request but don't require Gmail/password auth.
   * The email is attached to the order payload for the restaurant to contact the customer.
   */
  const signInAsGuestWithEmail = async (email) => {
    if (email) setCapturedEmail(email);
    if (currentUser) return currentUser;
    try {
      const result = await signInAsGuest();
      return result.user;
    } catch (error) {
      console.error("Guest login failed:", error);
      throw error;
    }
  };

  // Helper to ensure user is logged in (as guest if needed) before action
  const ensureGuestAuth = async () => {
    if (currentUser) return currentUser;
    try {
      const result = await signInAsGuest();
      return result.user;
    } catch (error) {
      console.error("Auto-guest login failed:", error);
      throw error;
    }
  };

  const signOutUser = async () => {
    setCapturedEmail('');
    await firebaseAuth.signOut();
  };

  const value = {
    currentUser,
    isAuthLoading,
    isGuest,
    capturedEmail,
    setCapturedEmail,
    signInAsGuest,
    signInAsGuestWithEmail,
    signOutUser,
    authModal,
    openAuthModal,
    closeAuthModal,
    ensureGuestAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
